import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Zap, LineChart, FileText, MessageSquare, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const PremiumPage = () => {
  const { user, getAuthHeaders, API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");

  const plans = [
    {
      id: "monthly",
      name: "Mensual",
      price: 9.99,
      period: "/mes",
      features: [
        "Ajuste automático de calorías por actividad",
        "Análisis semanal inteligente",
        "Predicción de cambio de peso",
        "Comidas frecuentes ilimitadas",
        "Exportación de datos en PDF",
        "Modo Coach Directo",
        "Gráficos avanzados"
      ]
    },
    {
      id: "annual",
      name: "Anual",
      price: 79.99,
      period: "/año",
      savings: "Ahorra 40€",
      features: [
        "Todo lo del plan mensual",
        "2 meses gratis",
        "Soporte prioritario",
        "Acceso anticipado a nuevas funciones"
      ]
    }
  ];

  const premiumFeatures = [
    {
      icon: <Zap className="w-5 h-5" />,
      title: "Ajuste inteligente",
      description: "Calorías ajustadas automáticamente según tu entrenamiento y actividad diaria"
    },
    {
      icon: <LineChart className="w-5 h-5" />,
      title: "Análisis avanzado",
      description: "Patrones semanales, tendencias y predicciones personalizadas"
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Exportación PDF",
      description: "Descarga informes completos de tu progreso nutricional"
    },
    {
      icon: <MessageSquare className="w-5 h-5" />,
      title: "Modo Coach",
      description: "Feedback directo y orientado a resultados reales"
    }
  ];

  const handleSubscribe = async () => {
    if (user?.is_premium) {
      toast.info("Ya tienes una suscripción activa");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/checkout/create`,
        {
          plan_type: selectedPlan,
          origin_url: window.location.origin
        },
        { headers: getAuthHeaders() }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Error al procesar el pago");
      setLoading(false);
    }
  };

  if (user?.is_premium) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6" data-testid="premium-page">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-3xl p-8 text-center"
          >
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Crown className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="font-heading text-3xl font-semibold mb-4">
              Eres miembro Premium
            </h1>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Disfruta de todas las funciones avanzadas de NutriChat AI.
              Gracias por tu apoyo.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {premiumFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 bg-card rounded-full text-sm"
                >
                  <span className="text-primary">{feature.icon}</span>
                  <span>{feature.title}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="premium-page">
        {/* Header */}
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Desbloquea todo el potencial
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
              Hazte Premium
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Lleva tu nutrición al siguiente nivel con análisis avanzados, 
              predicciones personalizadas y herramientas exclusivas.
            </p>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {premiumFeatures.map((feature, index) => (
            <div 
              key={index}
              className="bg-card border border-border/50 rounded-2xl p-5 hover-lift"
            >
              <div className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center mb-3 text-primary">
                {feature.icon}
              </div>
              <h3 className="font-semibold mb-1">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Pricing Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto"
        >
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative bg-card border-2 rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                selectedPlan === plan.id 
                  ? "border-primary shadow-lg" 
                  : "border-border/50 hover:border-border"
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.savings && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                  {plan.savings}
                </span>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg">{plan.name}</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === plan.id ? "border-primary bg-primary" : "border-muted-foreground"
                }`}>
                  {selectedPlan === plan.id && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
              </div>
              
              <div className="mb-4">
                <span className="font-heading text-4xl font-bold">{plan.price}€</span>
                <span className="text-muted-foreground">{plan.period}</span>
              </div>
              
              <ul className="space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            size="lg"
            className="rounded-full px-12 shadow-lg hover:shadow-xl transition-all"
            data-testid="subscribe-btn"
          >
            {loading ? "Procesando..." : `Suscribirme por ${plans.find(p => p.id === selectedPlan)?.price}€${plans.find(p => p.id === selectedPlan)?.period}`}
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Cancela en cualquier momento. Sin compromisos.
          </p>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default PremiumPage;
